import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  FileText,
  Filter,
  Search,
  Loader2,
  AlertCircle,
  Upload,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDocumentAuditTab = ({ onTotalChange }) => {
  const [logs, setLogs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);

  // Filters
  const [propertyFilter, setPropertyFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    fetchProperties();
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/properties`, {
        withCredentials: true,
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setFiltering(true);
      const params = new URLSearchParams();
      
      if (propertyFilter) params.append('property_id', propertyFilter);
      if (actionFilter) params.append('action', actionFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('limit', itemsPerPage.toString());
      params.append('skip', ((currentPage - 1) * itemsPerPage).toString());

      const response = await axios.get(
        `${API_URL}/api/documents/audit?${params.toString()}`,
        { withCredentials: true }
      );
      setLogs(response.data.logs || []);
      const newTotal = response.data.total || 0;
      setTotal(newTotal);
      onTotalChange?.(newTotal);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
      setFiltering(false);
    }
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchAuditLogs();
  };

  const handleClearFilters = () => {
    setPropertyFilter('');
    setActionFilter('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
    setTimeout(() => fetchAuditLogs(), 0);
  };

  useEffect(() => {
    if (!loading) {
      fetchAuditLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const getPropertyTitle = (propertyId) => {
    const property = properties.find((p) => p.id === propertyId);
    return property ? property.title : propertyId || 'N/A';
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'upload':
        return <Upload className="w-4 h-4 text-green-600" />;
      case 'delete':
        return <Trash2 className="w-4 h-4 text-red-600" />;
      case 'replace':
        return <RefreshCw className="w-4 h-4 text-blue-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'upload':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'delete':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'replace':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label>Property</Label>
              <Select value={propertyFilter} onValueChange={setPropertyFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All properties" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Action</Label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upload">Upload</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="replace">Replace</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Date From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div>
              <Label>Date To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <Button
                onClick={handleApplyFilters}
                disabled={filtering}
                className="w-full sm:flex-1"
              >
                {filtering ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Apply
              </Button>
              <Button
                className="w-full sm:w-auto"
                variant="outline"
                onClick={handleClearFilters}
                disabled={filtering}
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Audit Logs
            </span>
            <Badge variant="outline">{total} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <AlertCircle className="w-12 h-12 mb-4" />
              <p>No audit logs found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-4 font-medium text-sm">
                        Timestamp
                      </th>
                      <th className="text-left p-4 font-medium text-sm">
                        Action
                      </th>
                      <th className="text-left p-4 font-medium text-sm">
                        Document Type
                      </th>
                      <th className="text-left p-4 font-medium text-sm">
                        Filename
                      </th>
                      <th className="text-left p-4 font-medium text-sm">
                        Property
                      </th>
                      <th className="text-left p-4 font-medium text-sm">
                        Performed By
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-b hover:bg-gray-50">
                        <td className="p-4 text-sm">
                          <div>
                            <div className="font-medium">
                              {format(parseISO(log.timestamp), 'MMM d, yyyy')}
                            </div>
                            <div className="text-xs text-gray-500">
                              {format(parseISO(log.timestamp), 'h:mm:ss a')}
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-sm">
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            <Badge
                              className={`capitalize ${getActionBadgeColor(
                                log.action
                              )}`}
                            >
                              {log.action}
                            </Badge>
                          </div>
                        </td>
                        <td className="p-4 text-sm">
                          <Badge variant="outline" className="capitalize">
                            {log.document_type.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="p-4 text-sm font-medium">
                          {log.original_filename}
                        </td>
                        <td className="p-4 text-sm">
                          {getPropertyTitle(log.property_id)}
                        </td>
                        <td className="p-4 text-sm">
                          <div>
                            <div className="font-medium text-gray-900">
                              {log.performed_by_name || 'Unknown User'}
                            </div>
                            {log.performed_by_email && (
                              <div className="text-xs text-gray-500">
                                {log.performed_by_email}
                              </div>
                            )}
                            <Badge variant="secondary" className="text-xs capitalize mt-1">
                              {log.performed_by_role}
                            </Badge>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * itemsPerPage + 1}-
                    {Math.min(currentPage * itemsPerPage, total)} of {total} logs
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Button
                      className="w-full sm:w-auto"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <div className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button
                      className="w-full sm:w-auto"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Read-Only Audit Log</p>
              <p>
                This log tracks all document operations (upload, delete, replace).
                It is for audit and compliance purposes only. No restoration
                or file recovery is available.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
